// https://mui.com/toolpad/core/react-dashboard-layout/
// https://mui.com/toolpad/core/react-page-container/

import { useState, useEffect, useMemo, lazy, Suspense } from "react";

import axios from "axios";

import LoadingBar from '@/components/LoadingBar';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import { createTheme } from '@mui/material/styles';
// to get dark/light depending on the system
//import { useColorScheme } from '@mui/material/styles';

import { getMenu, getMenuFromPath, getMenuBreadcrumbs, getMenuTitle } from "@/utils/Menu";

import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout, DashboardSidebarPageItem, DashboardHeader } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';

//import { useActivePage } from '@toolpad/core/useActivePage';
//import invariant from 'invariant';

import Stack from '@mui/material/Stack';
import { Account } from '@toolpad/core/Account';

//import { BrowserRouter as Link } from "react-router-dom";
import { 
    getTableParam, 
    getNeedsSelection, 
    getFieldSelection, 
    checkIfItemView
} from "@/utils/Table";
import { checkNewRender } from '@/utils/Render';

import {
    Language as LanguageIcon,
    Sunny as LightModeIcon,
    DarkMode as DarkModeIcon,
    Notifications as NotificationsIcon,
    Help as HelpIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';

//import CompanyModal from "@/modals/CompanyModal";
import CompanyIcon from '@mui/icons-material/Business';

import { LoginMenu } from "@/components/LoginMenu";

import { formatValuesReceived, formatValuesToSend, getEditableColumn } from '@/utils/Columns';

const Chip = lazy(() => import('@mui/material/Chip'));
import Button from '@/components/Button';

import { checkFullPage, checkCompany } from '@/utils/Settings';
import { goTicketURL, isProd, getServerType } from '@/utils/URL';
import { getTableConfig, getNodesData } from '@/utils/Table';

//import { getDarkMode, setDarkMode } from '@/utils/Settings';
import { getErrorMessage } from '@/utils/Errors';

import { getFormData } from '@/utils/Format';
import { setParam, getParam } from '@/utils/Cookies';

import InformationModal from '/src/modals/InformationModal';
import UrlViewerModal from '/src/modals/UrlViewerModal';

const Legal = lazy(() => import('/src/pages/Legal'));
const Reset = lazy(() => import('/src/pages/Password/Reset'));
const NotFound = lazy(() => import('/src/pages/NotFound'));
const Home = lazy(() => import('/src/pages/Home'));
const Profile = lazy(() => import('/src/pages/Profile'));
const Versions = lazy(() => import('/src/pages/Versions'));
const Tickets = lazy(() => import('/src/pages/Tickets/Tickets'));
const Notifications = lazy(() => import('/src/pages/Notifications/Notifications'));
const GenericTable = lazy(() => import('/src/pages/GenericTable'));
const Reports = lazy(() => import('/src/pages/Reports'));
const Resources = lazy(() => import('/src/pages/Resources/Resources'));
//const Employees = lazy(() => import('/src/examples/Employees'));
//const Users = lazy(() => import('/src/examples/Users'));

function PageContent({
    t,language,router,menuConfig,user,setUser,apiUrl,token,sendMessage,
    getData,setData,deleteData,
    getDataReport,handleLogin,fullScreen,fullPage,setFullPage,openUrlModal
}){ 

    const path = router.pathname;  

    // check if user has accepted the conditions. if not go that page
    if(!user?.legal){
        return (            
            <Suspense>
                <Legal                    
                    t={t}
                    language={language}                    
                    router={router}
                    user={user}
                    setUser={setUser}
                    apiUrl={apiUrl}
                    token={token}
                    sendMessage={sendMessage} 
                    handleLogin={handleLogin}
                />                                    
            </Suspense>
        );
    }

    // if user forced to change password
    if(user?.reset){
        return (            
            <Suspense>
                <Reset                    
                    t={t}
                    language={language}
                    router={router}
                    user={user}
                    setUser={setUser}
                    token={token}
                    apiUrl={apiUrl}                    
                    sendMessage={sendMessage} 
                    handleLogin={handleLogin}
                />                                    
            </Suspense>
        );
    }

    // get menu option from path
    // just in case there are tabs on the menu or we are on item details
    const menu = getMenuFromPath(user,menuConfig,path);    

    // check if the user has permission on the menu
    if(menu === null){    
        return (
            <Suspense>
                <NotFound t={t} router={router}/>
            </Suspense>
        );
    }

    // if intermediate menus, show buttons with the submenus
    if(menuConfig[menu]?.children && menuConfig[menu].children.length > 0){

        const itemConfig = menuConfig[menu];

        return (
            itemConfig.children?.map((child) => (        
                <Suspense key={child?.path}>                  
                    <Button
                        id={child?.path}
                        className='!my-3 !py-2'
                        text={child?.title}
                        type='appoutlined'
                        onClick={() => router.navigate('/'+child?.path)}
                    />                    
                </Suspense>
            ))
        )
    }
    
    switch(menu){

        // to check the versions
        case 'versions':
            return ( 
                <Suspense>
                    <Versions/>
                </Suspense>
            );
            //break;

        case '': // home goes to profile
        case 'profile':
        case 'forgot':    // if trying to go to recover password but already logged in

            return (
                <Suspense>
                    <Profile 
                        t={t} 
                        language={language}
                        user={user}
                        setUser={setUser}
                        router={router}
                        apiUrl={apiUrl}                        
                        token={token}
                        sendMessage={sendMessage}
                        handleLogin={handleLogin}
                        fullScreen={fullScreen} 
                        openUrlModal={openUrlModal}                       
                    />
                </Suspense>
            );
            //break;

        case 'tickets':
            return (
                <Suspense>
                    <Tickets 
                        t={t}
                        language={language}
                        user={user}    
                        apiUrl={apiUrl}                        
                        token={token}
                        router={router}           
                        getData={getData}
                        setData={setData}
                        deleteData={deleteData}                        
                        sendMessage={sendMessage}
                        handleLogin={handleLogin}
                        fullScreen={fullScreen}
                        fullPage={fullPage}
                        setFullPage={setFullPage}
                        openUrlModal={openUrlModal}
                    />
                </Suspense>
            );
            //break; 

        case 'notifications': 
                
            return (
                <Suspense>
                    <Notifications 
                        t={t}  
                        router={router}
                        apiUrl={apiUrl}
                        token={token}
                        handleLogin={handleLogin}
                        language={language}
                        user={user}
                        getData={getData}
                        setData={setData}
                        deleteData={deleteData}
                        sendMessage={sendMessage}   
                        fullScreen={fullScreen}
                        fullPage={fullPage}    
                        setFullPage={setFullPage} 
                        openUrlModal={openUrlModal}                                        
                    />
                </Suspense>
            );
            //break;           

        // report for planifications
        case 'planifications/planification_reports':
          
            return (
                <Suspense>
                    <Reports
                        t={t}                            
                        user={user}
                        apiUrl={apiUrl}                        
                        token={token}
                        router={router}
                        report='planification_reports'
                        getDataReport={getDataReport}  
                        language={language}                            
                        sendMessage={sendMessage}
                        handleLogin={handleLogin}                        
                        openUrlModal={openUrlModal}  
                        fullScreen={fullScreen}         
                        fullPage={fullPage}     
                        setFullPage={setFullPage}       
                    />
                </Suspense>
            );
            
            // break;

        case 'resources/resource_rentals':

            return (
                <Suspense>
                    <Resources 
                        t={t}
                        language={language}
                        user={user}    
                        apiUrl={apiUrl}                        
                        token={token}
                        router={router}                                   
                        sendMessage={sendMessage}
                        handleLogin={handleLogin}
                        fullScreen={fullScreen}                        
                        openUrlModal={openUrlModal}
                    />
                </Suspense>
            );
            
            // break;

        default:

            // generic table

            // get table            
            var table = menu.split('/').pop();

            return (                
                <Suspense>
                    <GenericTable 
                        t={t}
                        language={language}
                        user={user}
                        token={token}
                        handleLogin={handleLogin}                       
                        router={router}
                        apiUrl={apiUrl}
                        table={table}
                        getData={getData}
                        setData={setData}
                        deleteData={deleteData}                        
                        sendMessage={sendMessage}        
                        fullScreen={fullScreen}  
                        fullPage={fullPage}      
                        setFullPage={setFullPage} 
                        openUrlModal={openUrlModal}      
                    />
                </Suspense>            
            );
            
            //break;
    }
}

const theme = createTheme({
    /*
    colorSchemes: { 
        light: true,
        dark: true
    },*/
    cssVariables: {        
        // <html lang="en" data-toolpad-color-scheme="dark">
        //colorSchemeSelector: 'data-toolpad-color-scheme',
        colorSchemeSelector: 'class',
    },
    defaultColorScheme: /*getDarkMode() ? 'dark' :*/ 'light',
    colorSchemes: {
        light: {
            palette: {
                background: {
                    //default: '#F9F9FE', // content
                    //paper: '#810000', // left and top bar
                }
            }
        }/*,
        dark: {
            palette: {
                background: {
                    //default: '#2A4364',
                    //paper: '#112E4D',
                }
            }
        }
        */
    },
    components: {
        // avoid changing alert color
        MuiAlert: {
            styleOverrides: {
                root: {
                    '--Paper-overlay': '0'
                },
                standardSuccess: {
                    '--Paper-overlay': '0',
                    backgroundColor: '#E6F4EA', // fixed light background
                    color: '#1E4620',            // fixed text
                },
                standardError: {
                    '--Paper-overlay': '0',
                    backgroundColor: '#FDECEA',
                    color: '#611A15',
                },
                standardWarning: {
                    '--Paper-overlay': '0',
                    backgroundColor: '#FFF4E5',
                    color: '#663C00',
                },
                standardInfo: {
                    '--Paper-overlay': '0',
                    backgroundColor: '#E8F4FD',
                    color: '#0C3C60',
                }
            }
        },
        MuiToolbar: {
            styleOverrides: {
                root: {    
                    marginY: 0,                
                    paddingY: 0,
                    minHeight: '50px',
                    //padding: '0 !important',
                    //paddingLeft: '0 !important',
                    //paddingRight: '0 !important',                    
                    //margin: 0
                },                
                // regular: {
                //     minHeight: 48,
                // },
            }
        }
    }
    /*
    components: {
        PageContainer: {
            styleOverrides: {
                root: ({ theme }) => ({
                    //color: '#FF0000'
                    backgroundColor: theme?.palette?.mode === 'light' ? '#FF0000' : '#000000 !important'                     
                })
            }
        }
    }*/
    /*,
    components: {
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: '#FFFFFF', // menu links                            
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: { 
                    color: '#FFFFFF' // menu links
                }
            }
        }
    }*/
    /*
    breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 600,
          lg: 1200,
          xl: 1536,
        },
    },*/
});

// top bar
function ToolbarActions({t,user,changeLanguage,router,menuConfig,sendMessage,fullScreen}) {

    //const { mode/*, setMode*/ } = useColorScheme();
    const [ mode ] = useState('light');

    // wait to be initialized
    /*
    if(!mode){
        return null;
    }
    */

    // save mode
    /*
    useEffect(() => {
        
        // to avoid getting by default sytem mode (on incognito)
        // and use always light by default
        const html = document.documentElement; 
        html.classList.remove('dark');
        html.classList.add(mode !== 'dark' ? 'light' : 'dark');

        if(mode){            
            setDarkMode(mode !== 'dark' ? false : true);
        }

    }, [mode]);
    */

    /*
    const changeDarkmode = () => {
        setMode(mode !== 'dark' ? 'dark' : 'light');
    }
    */

    return (
        <Stack direction="row">            
            <div className='max-sm:hidden items-center flex text-white text-center me-8'>                    
                <CompanyIcon fontSize='small'/>
                {/*
                <div className='max-sm:hidden ml-1 mr-2 mt-0 text-sm'>
                    {user?.company?.name}
                </div>                
                <div className='ml-1 mr-2 mt-0 text-sm'>
                    {user?.company?.name.substr(0,10)}...
                </div>
                */}   
                {user?.companies[user?.company]?.name}                 
            </div>
            <IconButton sx={{paddingRight:1}} onClick={fullScreen.active ? fullScreen.exit : fullScreen.enter}>
                {
                    fullScreen.active ?
                        <FullscreenExitIcon/>
                    :
                        <FullscreenIcon/>
                }
            </IconButton> 
            <IconButton sx={{paddingRight:1}} onClick={() => router?.navigate('/notifications')}>
                <NotificationsIcon/>
            </IconButton> 
            {/*
            <IconButton sx={{paddingRight:1}} onClick={changeDarkmode}>
                {mode !== 'dark' ?  <LightModeIcon/> : <DarkModeIcon/>}
            </IconButton> 
            */}
            <IconButton sx={{paddingRight:1}} onClick={changeLanguage}>
                <LanguageIcon/>
            </IconButton>  
            <IconButton 
                sx={{paddingRight:1}} 
                onClick={() => goTicketURL(t,'/tickets',user,router,menuConfig,sendMessage)}
            >
                <HelpIcon/>
            </IconButton>              
            <Account                
                sx={{
                    // add marginTop for small screens
                    /*
                    '& .MuiStack-root': {
                        top: '100px',
                        '@media (max-width: 600px)': {
                            top: '160px'
                        }
                    }
                    */
                }}
                localeText={{
                    //accountSignInLabel: '',
                    accountSignOutLabel: t('logout.button'),
                }}
                slots={{
                    //popoverContent: LoginMenu
                    popoverContent: () => (
                        <LoginMenu darkmode={mode === 'dark' ? true : false}/>
                    )
                }}
            />
        </Stack>
    );
}

// footer on the left menu
function SidebarFooter({t,handleLegal}){

    return (
        <div id='footer'>
            {/*`© 2021 - ${new Date().getFullYear()} Victor Tormo S.L.U. +34 96 221 44 46 info@victortormo.com`*/}
            <div>
                © {import.meta.env.VITE_COMPANY_YEAR} {import.meta.env.VITE_FOOTER}
            </div>
            {/*
            <div>
                <a href={'tel:'+import.meta.env.VITE_COMPANY_PHONE}>{import.meta.env.VITE_COMPANY_PHONE}</a> 
                <a href={'mailto:'+import.meta.env.VITE_COMPANY_EMAIL}>{import.meta.env.VITE_COMPANY_EMAIL}</a>
            </div>
            */}
            <div id='footer-legal' onClick={handleLegal}>
                {t('legal.policy.title')}
            </div>
        </div>
    );
}

// to set the menu and show on the title
/*
function useChangePath(initialPath) {

    const [pathname, setPathname] = useState(initialPath);

    const router = useMemo(() => {
        return {
            pathname,
            searchParams: new URLSearchParams(),
            navigate: (path) => setPathname(String(path)),
        };
    }, [pathname]);

    return router;
}
*/

// to separate path and params
function parsePath(pathString){

    let cleanedPathString = pathString;
    
    // changes /option/ or /option/? => /option
    cleanedPathString = cleanedPathString.replace(/\/(?=\?|$)/, '');

    // separate path and params
    const [ pathname, queryString ] = cleanedPathString.split('?');
  
    return {
        // to ensure pathname is not empty if url was only ?
        pathname: pathname || '/',        
        searchParams: new URLSearchParams(queryString || ''), 
        // save query string with ?
        search: queryString ? `?${queryString}` : '', 
    };

}

// to define the navigation router
function useChangePath() {
    
    const initialUrl = window.location.pathname + window.location.search;

    const [ pathString, setPathString ] = useState(initialUrl);

    const router = useMemo(() => {
        
        const { pathname, searchParams, search } = parsePath(pathString);

        return {            
            pathname,            
            searchParams,            
            search,            
            navigate: (newPath) => {
                
                // clean final / from path
                const { pathname: cleanedPathname, search: cleanedSearch } = parsePath(newPath);
                const finalPath = `${cleanedPathname}${cleanedSearch}`;

                setPathString(finalPath);
                
                // sync browser url
                window.history.pushState(null, '', finalPath);

                //if you want to replace the current entry on the history
                // window.history.replaceState(null, '', newPath);
            }
        };
    }, [ pathString ]);

    return router;
}

export default function Dashboard({t,i18n,apiUrl,handleLogin,token,user,setUser,sendMessage,setShowLanguage,fullScreen}){

    // default menu
    //const router = useChangePath(window.location.pathname);
    const router = useChangePath();

    // to show the page with top bar and menu or only the content
    const [ fullPage, setFullPage ] = useState(checkFullPage(router.searchParams));

    // to show menu collapsed or not
    const [ collapsed ] = useState(getParam("collapsed"));

    useMemo(() => {        
        // check if company on the url to go to a concrete database
        checkCompany(router,user,setUser,handleLogin);
    });

    const [ menu, setMenu ] = useState([]);
    const [ menuConfig, setMenuConfig ] = useState([]);

    // to avoid re-calling twice the methods
    useEffect(() => {
        const [ menuData, menuConfigData ] = getMenu(t,user);
        setMenu(menuData);
        setMenuConfig(menuConfigData);
    },[t,user]);

    const [ session ] = useState({t:t,user:user,setUser:setUser});

    const authentication = useMemo(() => {
        return {
            signIn: () => {},
            /*
            signIn: () => {
                setSession({
                  user: {
                    name: 'Bharat Kashyap',
                    email: 'bharatkashyap@outlook.com',
                    image: 'https://avatars.githubusercontent.com/u/19550456',
                  },
                });
            },*/
            signOut: () => {
                handleLogin(null,null,true);
            },
        };
    }, [handleLogin]);

    const [ showLegal, setShowLegal ] = useState(false);
    const handleLegal = () => {
        setShowLegal(true);
    }

    const changeLanguage = () => {
        setShowLanguage(true);
    };

    /*
    const [ showCompany, setShowCompany ] = useState(false);
    const [ currentCompany, setCurrentCompany ] = useState(user?.company);

    const changeCompany = (event) => {
        setShowCompany(true);
    };

    const handleCompany = (iso) => {  
        setShowCompany(false); // close modal
        window.location.reload(); // reload page to send the new database to the backend
    };
    */

    // to open a url on a modal
    const [ showUrlModal, setShowUrlModal ] = useState(false);
    const [ urlModal, setUrlModal ] = useState('');

    const [ urlIsFile, setUrlIsFile ] = useState(true);
    const [ urlFileType, setUrlFileType ] = useState(null);
    const [ urlFileMime, setUrlFileMime ] = useState(null);
    const [ urlFileName, setUrlFileName ] = useState('');
    const [ urlTitle, setUrlTitle ] = useState('');
    const [ urlDisclaimer, setUrlDisclaimer ] = useState('');
    const [ urlDownload, setUrlDownload ] = useState(false);
    const [ urlExternal, setUrlExternal ] = useState(false);
    const [ urlEmbedded, setUrlEmbedded ] = useState(false);

    // url is the url to open
    // file is true/false if it is a file. if not it is a page
    // if file it can have type, mime and name. if not, we try to get from url
    // download to can download the file and external to open externally from the modal
    // embedded is to use native pdf viewer
    const openUrlModal = (url,file = null, type = null, mime = null, name = '', title = '', disclaimer = '', download = false, external = false, embedded = true) => {
        
        setUrlModal(url);
        setUrlIsFile(file);
        setUrlFileType(type);
        setUrlFileMime(mime);
        setUrlFileName(name);
        setUrlTitle(title);
        setUrlDisclaimer(disclaimer);
        setUrlDownload(download);
        setUrlExternal(external);
        setUrlEmbedded(embedded);

        setShowUrlModal(true);
    };

    const handleOpenUrlModal = (open) => {
        // if closing, clean url
        if(!open){
            setUrlModal('');
            setShowUrlModal(false);
        }
    };

    // to get the data for the tables. defined here because we can re-use it
    const getData = (path,table,setLoading,setColumns,setTableRows,setConfig,resetData,setResponse,setEditable = null,
        modalDefault=null,setModal = null,setItemDetails = null,tableItemId = null) => {

        setLoading(true);
        resetData();

        // default
        var apiPath = '/'+table+'/list';
        /*
        switch(table){

            case 'notifications':
                //apiPath = '/notifications';
                break;
        }
        */

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: i18n.language,
                companyuser: user?.company
            }  
        };

        // check if we are on submenu that needs a selection on parent menu 
        var needsSelection = getNeedsSelection(path,table);

        // if needs selection but nothing selected, do not retrieve data 
        var retrieveData = true;   

        // if only getting one item
        if(tableItemId){
            config.params.selectedField = 'id';
            config.params.selectedId = tableItemId;
        }
        else{

            // if submenu that needs selection
            if(needsSelection && needsSelection !== table){
                
                var paramInfo = getTableParam(user,needsSelection,'selected');

                if(paramInfo?.id){

                    // get the field to be filtered on backend
                    var fieldName = getFieldSelection(path,table);

                    config.params.selectedField = fieldName;
                    config.params.selectedId = paramInfo?.id;
                }
                else{
                    retrieveData = false;
                }
            }
        }
        
        if(retrieveData){
            
            var errorMessage = t('actions.get.error');

            axios.get(apiUrl+apiPath,config)
            .then(function (response) {

                if(response?.data){

                    const responseData = response.data;

                    if(setResponse){
                        setResponse(responseData);
                    }

                    // if result is ok (1)
                    if(responseData?.result && responseData && Object.hasOwn(responseData,'data')){

                        const data = responseData.data;

                        // get if table editable and modal type                    
                        // by default true/true
                        if(setEditable){
                            
                            var isEditable = true;                            
                            //if(responseData.data?.config?.hasOwnProperty('editable')){                            
                            if(data?.config && data?.config && Object.hasOwn(data?.config,'editable')){
                                isEditable = data.config.editable ? true : false;
                            }
                            
                            setEditable(isEditable);
                        }

                        // check edition mode if editable
                        // if not editable, not necessary, only view by modal
                        // it can be not editable but have the option to add, for example, like tickets
                        //if(isEditable){

                            if(setModal){

                                // if saved value, use it
                                var modalConfig = getTableConfig(user,table,'modal');
                                if(typeof modalConfig !== 'undefined' && modalConfig !== null){                            
                                    setModal(modalConfig ? true : false);
                                }    
                                else{
                                    // if not saved, if value from server, set it
                                    //if(data?.config?.hasOwnProperty('modal')){  
                                    if(data?.config && data?.config && Object.hasOwn(data?.config,'modal')){
                                        setModal(data.config.modal ? true : false);
                                    }
                                    else{
                                        // get default config                                               
                                        setModal(modalDefault !== null ? modalDefault : true);
                                    }
                                }
                            }
                        //}

                        // get items
                        const dataItems = data?.items;     

                        // get the columns with format
                        let dataFormatted;
                        if(data?.columns){
                            
                            const columns = data.columns;
                            setColumns(columns);

                            // format data to get it ready for the ag grid
                            dataFormatted = formatValuesReceived(t,table,columns,dataItems);
                        }
                        else{
                            setColumns([]);
                            dataFormatted = dataItems;
                        }                        
                        
                        setTableRows(dataFormatted);

                        // save config
                        setConfig(data?.config);

                        // check if we are on /view/N or /fullview/N to show only the info for that item
                        // although we get all the data because it makes no sense to call backend only for 1 row and
                        // call it later to go to the table with all the rows                            
                        if(setItemDetails){
                            const viewId = checkIfItemView();
                            if(viewId){
                                const filter = dataFormatted?.filter(item => item?.id === viewId);
                                if(filter && filter?.length > 0){
                                    setItemDetails(filter?.pop());
                                }
                                else{
                                    // to differentiate from null
                                    setItemDetails({});
                                }
                            }
                        }
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();
                        }
                        else{

                            if(responseData?.data?.reasons){                            
                                const reasons = responseData?.data?.reasons;
                                errorMessage = getErrorMessage(t,table,reasons);
                            }
                            else{
                                // generic error
                                if(responseData?.data?.error){
                                    errorMessage = t(table+'.errors.'+responseData.data.error);
                                }
                            }

                            sendMessage(errorMessage,'error');
                        }

                        resetData();
                    }
                }
                else{
                    resetData();
                    sendMessage(errorMessage,'error');
                    
                    if(setResponse){
                        setResponse(null);
                    }
                }

                setLoading(false);
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){                    
                    handleLogin();
                }
                else{                   
                    sendMessage(errorMessage,'error');
                }

                resetData();

                if(setResponse){
                    setResponse(null);
                }
            });
        }
    }

    // to set the data for the tables. defined here because we can re-use it
    // used to add or edit
    const setData = (action,path,table,api,node,modal,data,columns,setTableRows,setSavedEnabled = null,setAddDisabled=null,handleCloseItemModal = null,setRender = null, handleTableItemSelected = null) => {

        // action can be:
        // save if saving info with button, etc.
        // enter if editing inline and clicking enter key

        var apiPath = '/'+table;
        /*
        switch(table){

            case 'notifications':
                apiPath = '/notification';
                break;

            case 'employees':
                apiPath = '/employee';
                break;
        }
        */

        const config = {
            headers: { Authorization: `Bearer ${token}` },
            //'content-type': 'multipart/form-data',
        };

        const id = data?.id;

        // format data to be sent to the api          
        const dataFormatted = formatValuesToSend(t,table,columns,data);

        /*
        var send = {
            language: i18n.language,
            companyuser: user?.company,
            data: dataFormatted
        };
        */

        const params = {
            language: i18n.language,
            companyuser: user?.company
        };

        // if adding item, check if we are on submenu that needs a selection on parent menu 
        // if editing no problem because the selected item is already there 
        var needsSelection = false;   
        var selectionInfo = false;        
        if(!id){

            needsSelection = getNeedsSelection(path,table);

            // if submenu, send selected to the backend to autofill the value
            if(needsSelection && needsSelection !== table){

                selectionInfo = getTableParam(user,needsSelection,'selected');

                // get the field to be filtered on backend
                var fieldName = getFieldSelection(path,table);

                //send.selectedField = fieldName;
                //send.selectedId = selectionInfo?.id;
                params.selectedField = fieldName;
                params.selectedId = selectionInfo?.id;
            }
        }

        // transform data to send with formData
        // just in case we have files              
        const send = getFormData(columns,dataFormatted,params);

        // if id null, it means we are adding
        let errorMessage;
        let url;
        let ajax;
        if(!id){
            // inserting
            errorMessage = t('actions.add.error');
            url = apiUrl+apiPath+'/add';
            ajax = axios.post(url,send,config);
        }
        else{
            // updating
            errorMessage = t('actions.edit.error');
            url = apiUrl+apiPath+'/edit';

            // cannot use put because if there are files it does not work
            //ajax = axios.put(url,send,config);
            ajax = axios.post(url,send,config);
        }
        
        ajax.then(function (response) {
            
            if(response?.data){
          
                var responseData = response.data;

                // if result is ok (1)                
                if(responseData?.result){              
                    
                    //sendMessage(t('actions.edit.success'),'success');

                    // format the data
                    const dataItems = [responseData.data.item];
                    const dataFormatted = formatValuesReceived(t,table,columns,dataItems);
                    
                    var newItemData = null;
                    if(dataFormatted && dataFormatted.length > 0){
                        newItemData = dataFormatted.pop();
                    }

                    // if inline edition, change button from "save" to "edit"
                    if(!modal){

                        // save data with the manually updated info and auto updated info
                        if(newItemData){
                            node?.setData(newItemData);
                        }

                        setSavedEnabled(false);
                        setAddDisabled(false);

                        // stop editing
                        //api.stopEditing();                        
                    }
                    else{                        
                        // if modal edition

                        // if adding item, node is null
                        if(!id){
                            
                            // save data with the manually updated info and auto updated info
                            if(newItemData){                                    
                                api?.applyTransaction({
                                    "add": [newItemData]
                                });
                            }
                        }
                        else{
                            // if editing, update node

                            // save data with the manually updated info and auto updated info
                            if(newItemData){

                                // it works for every table but only checked on tickets
                                // for some strange reason, for /tickets/view/N                                                                        
                                // if you use node.setDataValue it works but not with node.setData
                                const itemId = checkIfItemView();                   
                                if(itemId){                                            
                                    Object.keys(newItemData)?.map((key) => {
                                        node?.setDataValue(key,newItemData[key]);
                                    });                                        
                                    /*
                                    switch(table){

                                        case 'tickets':
                                            node?.setDataValue('open',newItemData?.open);
                                            node?.setDataValue('ticket_status_id',newItemData?.ticket_status_id);
                                            break;
                                    } 
                                    */                                                                       
                                }
                                else{
                                    node?.setData(newItemData);

                                    // re apply order after editing/adding
                                    api.refreshClientSideRowModel('sort');
                                }
                            }
                        }

                        // close modal   
                        if(handleCloseItemModal){                         
                            handleCloseItemModal(true);
                        }                        
                    }

                    // show confirmation message
                    sendMessage(t('actions.save.success'),'success');

                    // check if we are on parent table which needs selection to update data
                    // because maybe we changed the name
                    // check if we modified the row that was the selection
                    const operation = !id ? 'add' : 'edit';
                    checkNewRender(user,t,api,operation,router,table,newItemData,columns,sendMessage,setRender,handleTableItemSelected);

                    // if setTableRows, update the rows because the table is re-rendered if you change the tab or
                    // if we go to item details (tickets for example)
                    setTableRows(getNodesData(api));
                } 
                else{

                    if(responseData?.status && responseData.status === 401){
                        handleLogin();
                    }
                    else{                       
                        // to place the focus on it if inline edition
                        var errorColumn = null; 

                        if(responseData?.data?.reasons){
                            
                            const reasons = responseData?.data?.reasons;
                            
                            var keys = Object.keys(reasons);
                            if(keys){
                                errorColumn = keys[0];
                            }

                            errorMessage = getErrorMessage(t,table,reasons);
                        }
                        else{
                            // generic error
                            if(responseData?.data?.error){
                                errorMessage = t(table+'.errors.'+responseData.data.error);
                            }
                        }
                        
                        sendMessage(errorMessage,'error');

                        // if inline edition, change button from "save" to "edit"
                        if(!modal){   

                            // check if the first column with errors is editable to focus on it
                            // if not editable, get the editable column
                            const colKeyEdit = getEditableColumn(api,errorColumn);

                            // if action is enter, it removes editing mode by default, so change to edit mode again
                            if(action === 'enter'){

                                // if adding row and error, set null the id                                
                                if(!id){
                                    node?.setDataValue('id',null);
                                }

                                // show editing mode again on the row                        
                                api?.startEditingCell({
                                    rowIndex: node?.rowIndex,
                                    colKey: colKeyEdit
                                });
                            }
                            else{
                                // does not work when clicking save button
                                /*
                                // focus on error column
                                api?.setFocusedCell({
                                    rowIndex: node?.rowIndex,
                                    colKey: colKeyEdit
                                });
                                */ 
                            }

                            /*
                            // show editing mode again on the row

                            // if adding row and error, set null the id                                
                            if(!id){
                                node?.setDataValue('id',null);
                            }

                            api?.startEditingCell({
                                rowIndex: node?.rowIndex,
                                colKey: errorColumn
                            });
                            */
                        }
                        else{
                            // if modal edition, nothing to do
                        }
                    }
                }
            }
            else{
                sendMessage(errorMessage,'error');

                // if inline edition and adding, remove new row
                if(!modal){   
                                         
                    // set null the id
                    node?.setDataValue('id',null);

                    // remove row if we were adding   
                    if(!id){
                        api?.applyTransaction({
                            "remove": [node?.data]
                        });
                    }
                    
                    // stop editing
                    //api.stopEditing();
                }
                else{
                    // if modal edition, nothing to do
                }    
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();
            }
            else{
                //console.log(error)
                sendMessage(errorMessage,'error');

                // if inline edition and adding, remove new row
                if(!modal){   
                                         
                    // set null the id
                    node?.setDataValue('id',null);

                    // remove row if we were adding   
                    if(!id){
                        api?.applyTransaction({
                            "remove": [node?.data]
                        });
                    }

                    // stop editing
                    //api.stopEditing();
                }
                else{
                    // if modal edition, nothing to do
                } 
            }
        });
    }

    // delete data
    const deleteData = (path,table,selectedRows,api,columns,setTableRows,handleCloseItemModal = null,setRender = null, handleTableItemSelected = null) => {

        var apiPath = '/'+table+'/delete';
        /*
        switch(table){

            case 'notifications':
                //apiPath = '/notifications/delete';
                break;
        }
        */

        var selectedIDs = [];
        selectedRows?.map((row) => {
            selectedIDs.push(row?.id)
        });        

        const ids = selectedIDs.join(",");

        const config = {
            headers: { Authorization: `Bearer ${token}` },
            data: {
                language: i18n.language,
                companyuser: user?.company,
                ids: ids
            }
        };

        var errorMessage = t('actions.delete.error');

        axios.delete(apiUrl+apiPath,config)
        .then(function (response) {
            
            if(response?.data){
                          
                var responseData = response.data;

                // if result is ok (1)
                if(responseData?.result){

                    api?.applyTransaction({remove: selectedRows});

                    // if setTableRows, update the rows because the table is re-rendered if you change the tab or
                    // if we go to item details (tickets for example)
                    setTableRows(getNodesData(api));

                    // if modal edition, close modal
                    if(handleCloseItemModal){
                        handleCloseItemModal(true);                        
                    }

                    // check if we are on parent table which needs selection to update selection
                    // in case we delete the current selection  
                    checkNewRender(user,t,api,'delete',router,table,selectedIDs,columns,sendMessage,setRender,handleTableItemSelected);
                } 
                else{

                    if(responseData?.status && responseData.status === 401){
                        handleLogin();
                    }
                    else{                                        

                        if(responseData?.data?.reasons){                            
                            const reasons = responseData?.data?.reasons;
                            errorMessage = getErrorMessage(t,table,reasons,false,true);
                        }                        
                        else{
                            // generic error
                            if(responseData?.data?.error){
                                errorMessage = t(table+'.errors.'+responseData.data.error);
                            }
                        }
                        
                        sendMessage(errorMessage,'error');
                    }
                }
            }
            else{                
                sendMessage(errorMessage,'error');
            }
        })
        .catch(function (error){
            
            if(error?.status && error.status === 401){
                handleLogin();
            }
            else{                
                sendMessage(errorMessage,'error');
            }
        });
    }

    // retrieve data for report
    const getDataReport = (path,report,type,setResponse,setReportData,setDocumentsData) => {

        // default
        var apiPath = '/'+report+'/list';
        /*
        switch(report){

            case 'planification_reports':
                //apiPath = '/planification_reports/list';
                break;
        }
        */

        const config = {
            headers: { Authorization: `Bearer ${token}` },   
            // params is the same as using ?database=, query params      
            params: {
                language: i18n.language,
                companyuser: user?.company,
                type: type
            }  
        };

        // check if the report needs a parent selection
        var needsSelection = getNeedsSelection(path,report);

        // if needs selection but nothing selected, do not retrieve data 
        var retrieveData = true;   

        // if submenu that needs selection
        if(needsSelection && needsSelection !== report){
            
            var paramInfo = getTableParam(user,needsSelection,'selected');

            if(paramInfo?.id){

                // get the field to be filtered on backend
                var fieldName = getFieldSelection(path,report);

                config.params.selectedField = fieldName;
                config.params.selectedId = paramInfo?.id;
            }
            else{
                retrieveData = false;
            }
        }
        
        if(retrieveData){

            var errorMessage = t('actions.get.error');

            axios.get(apiUrl+apiPath,config)
            .then(function (response) {

                if(response?.data){

                    var responseData = response.data;

                    if(setResponse){
                        setResponse(responseData);                
                    }
              
                    // if result is ok (1)
                    if(responseData?.result && responseData?.data){
                        
                        if(type === 'charts'){
                            setReportData(responseData.data);
                        }
                        else{
                            // documents

                            // filter only files
                            responseData.data.documents = responseData.data?.documents?.filter(doc => doc?.file);
                            setDocumentsData(responseData.data);
                        }                        
                    } 
                    else{
                        if(responseData?.status && responseData.status === 401){
                            handleLogin();                        
                        }
                        else{

                            if(responseData?.data?.reasons){                            
                                const reasons = responseData?.data?.reasons;                                
                                errorMessage = getErrorMessage(t,report,reasons,false,false);
                            }                            
                            else{
                                // generic error
                                if(responseData?.data?.error){
                                    errorMessage = t(report+'.errors.'+responseData.data.error);
                                }
                            }
                            
                            //sendMessage(errorMessage,'error');

                            if(type === 'charts'){
                                setReportData(false); // false because if null it keeps loading the backdrop
                            }
                            else{
                                // documents
                                setDocumentsData(false);
                            }                            
                        }                    
                    }
                }
                else{
                    //sendMessage(errorMessage,'error');

                    if(setResponse){
                        setResponse(null);
                    }

                    if(type === 'charts'){
                        setReportData(false); // false because if null it keeps loading the backdrop
                    }
                    else{
                        // documents
                        setDocumentsData(false);
                    } 
                }
            })
            .catch(function (error){
                
                if(error?.status && error.status === 401){
                    handleLogin();                        
                }
                else{                        
                    sendMessage(errorMessage,'error');
                }
                
                if(setResponse){
                    setResponse(null);
                }

                if(type === 'charts'){
                    setReportData(false); // false because if null it keeps loading the backdrop
                }
                else{
                    // documents
                    setDocumentsData(false);
                } 
            });
        }
        else{
            sendMessage(t('actions.get.error'),'error');
        }
    }

    // to control what to do when choosing an item from the menu
    // if nested menu, renderPageItem is not called for the nested children
    const renderPageItem = (item/*, mini*/) => {

        //const active = useActivePage();
        const currentPath = router.pathname;//window.location.pathname;
        
        // if on a menu with nested children
        if(currentPath.includes(item?.segment) && item?.children && item.children.length > 0){
            return <DashboardSidebarPageItem item={item} expanded/>;
        }
        else{
            return <DashboardSidebarPageItem item={item} /*selected disabled mini={mini}*//>;
        }
    }

    //invariant(activePage, 'No navigation match');
    //const title = 'Item';
    //const path = `${activePage?.path}`;
    //const breadcrumbs = [...activePage.breadcrumbs, { title, path }];
    //const [ breadcrumbs, setBreadcrumbs ] = useState([...activePage.breadcrumbs, { title, path }]); 

    return (
        <>
        {/*
        {
            showCompany &&
            <Suspense>
                <CompanyModal 
                    t={t}
                    open={showCompany} 
                    setOpen={setShowCompany} 
                    company={currentCompany}
                    companies={user?.companies}
                    handleCompany={handleCompany}
                    fullScreen={fullScreen}
                />
            </Suspense>
        }
        */}        
        <InformationModal   
            t={t}                  
            open={showLegal} 
            setOpen={setShowLegal}                     
            title={t('legal.policy.title')}
            html={t('legal.policy.message')}
            fullScreen={fullScreen}
        />        
        <UrlViewerModal
            t={t}
            open={showUrlModal}
            setOpen={handleOpenUrlModal}
            url={urlModal}
            file={urlIsFile}
            type={urlFileType}
            mime={urlFileMime}
            name={urlFileName}
            title={urlTitle}
            download={urlDownload}
            external={urlExternal}
            embedded={urlEmbedded}
            disclaimer={urlDisclaimer}
            fullScreen={fullScreen}
        />
        {
            (menuConfig && Object.keys(menuConfig).length > 0) ?                
                <AppProvider                        
                    navigation={menu}
                    router={router}
                    theme={theme}
                    branding={{
                        logo: 
                            <>
                                <img src="/images/logo_small.png" className='h-10 sm:hidden'/>
                                <img src="/images/logo.png" className='sm:h-8 h-6 sm:block hidden'/>                                
                            </>,
                        title: !isProd() ? <div className='text-white text-[8px]'>{getServerType()}</div> : '',
                        homeUrl: '/',
                    }}                        
                    session={session}
                    authentication={authentication}
                >
                    <LoadingBar router={router}/>                    
                    <DashboardLayout    
                        hideNavigation={fullPage ? true : false}
                        renderPageItem={renderPageItem}
                        //disableCollapsibleSidebar
                        defaultSidebarCollapsed={collapsed ? true : false}
                        sidebarExpandedWidth={210}
                        slots={
                            fullPage ? 
                                {
                                    header: () => null
                                }
                            :
                                {
                                    header: (headerProps) => (
                                        <DashboardHeader
                                            {...headerProps}                                                  
                                            onToggleMenu={(isOpen) => {
                                                // save preference to keep when refreshing
                                                setParam('collapsed', isOpen ? 0 : 1);

                                                // toggle menu                                               
                                                headerProps.onToggleMenu(isOpen)
                                            }}
                                        />
                                    ),                                    
                                    toolbarActions: () => (
                                        <ToolbarActions                                         
                                            t={t}
                                            user={user}
                                            changeLanguage={changeLanguage}
                                            router={router}
                                            menuConfig={menuConfig}
                                            sendMessage={sendMessage}
                                            fullScreen={fullScreen}
                                        />
                                    ),
                                    //sidebarFooter: sidebarFooter
                                    sidebarFooter: () => (
                                        <SidebarFooter t={t} handleLegal={handleLegal}/>
                                    )
                                    //appTitle: CustomAppTitle,
                                    //toolbarActions: toolbarActions
                                    //toolbarAccount
                                }
                        }
                        sx={{
                            width:'100%',
                            height:'100dvh',
                            '& .MuiToolbar-root': { 
                              minHeight: fullPage ? '0 !important' : '50px !important'
                            }
                        }}
                    >
                        <PageContainer                                 
                            maxWidth={false}
                            sx={{
                                // to apply dark mode
                                backgroundColor: 'var(--mui-palette-background-default)'
                            }}
                            breadcrumbs={getMenuBreadcrumbs(t,router)}
                            title={getMenuTitle(t,router)}
                        >                                         
                            <PageContent
                                t={t}
                                language={i18n.language}
                                router={router}                                
                                menuConfig={menuConfig}
                                user={user}
                                setUser={setUser}
                                apiUrl={apiUrl} 
                                token={token}
                                sendMessage={sendMessage}
                                getData={getData}
                                setData={setData}
                                deleteData={deleteData}                                
                                getDataReport={getDataReport}   
                                handleLogin={handleLogin}
                                fullScreen={fullScreen}
                                fullPage={fullPage}
                                setFullPage={setFullPage}
                                openUrlModal={openUrlModal}
                            />
                        </PageContainer>
                    </DashboardLayout>                      
                </AppProvider> 
            :
                <></> 
        }   
        </>
    );
}